import React, { useState, useRef, useEffect, useCallback } from "react";

const DEFAULT_ZOOM_STEP = 0.3;
const DEFAULT_LARGE_ZOOM = 4;

const getXY = (e) => {
  if (e.touches && e.touches.length) {
    return { x: e.touches[0].pageX, y: e.touches[0].pageY };
  }
  return { x: e.pageX, y: e.pageY };
};

const LightBox = ({
  images = [],
  image,
  title,
  startIndex = 0,
  onClose,
  onNavigateImage,
  allowZoom = true,
  allowRotate = true,
  allowReset = true,
  keyboardInteraction = true,
  clickOutsideToExit = true,
  zoomStep = DEFAULT_ZOOM_STEP,
  doubleClickZoom = DEFAULT_LARGE_ZOOM,
  buttonAlign = "flex-end",
  showTitle = true,
}) => {
  const multi = images.length > 0;
  const [state, setState] = useState({
    x: 0,
    y: 0,
    zoom: 1,
    rotate: 0,
    current: startIndex,
    moving: false,
    loading: true,
  });

  const { x, y, zoom, rotate, current, moving, loading } = state;
  const contRef = useRef(null);
  const imageRef = useRef(null);
  const initPos = useRef({ x: 0, y: 0 });
  const lastPos = useRef({ x: 0, y: 0 });
  const rafId = useRef(null);

  const currentImage = multi ? images[current]?.url ?? images[current] : image;
  const currentTitle = multi ? images[current]?.title ?? "" : title ?? "";

  const transform = `translate3d(${x}px, ${y}px, 0px) scale(${zoom}) rotate(${rotate}deg)`;

  const setPartialState = (update) =>
    setState((prev) => ({ ...prev, ...update }));

  const resetZoom = useCallback(
    () => setPartialState({ x: 0, y: 0, zoom: 1 }),
    []
  );

  const shouldShowReset = x !== 0 || y !== 0 || zoom !== 1 || rotate !== 0;

  const navigateImage = useCallback(
    (direction, e) => {
      e.stopPropagation();
      if (!multi) return;

      let nextIndex = direction === "next" ? current + 1 : current - 1;

      nextIndex = (nextIndex + images.length) % images.length;

      setPartialState({
        current: nextIndex,
        x: 0,
        y: 0,
        zoom: 1,
        rotate: 0,
        loading: true,
      });

      onNavigateImage?.(nextIndex);
    },
    [current, images.length, multi, onNavigateImage]
  );

  const startMove = (e) => {
    if (zoom <= 1) return;
    const xy = getXY(e);
    initPos.current = {
      x: xy.x - lastPos.current.x,
      y: xy.y - lastPos.current.y,
    };
    setPartialState({ moving: true });
  };

  const duringMove = (e) => {
    if (!state.moving) return;
    const xy = getXY(e);
    const newX = xy.x - initPos.current.x;
    const newY = xy.y - initPos.current.y;
    lastPos.current = { x: newX, y: newY };

    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() =>
      setPartialState({ x: newX, y: newY })
    );
  };

  const endMove = () => {
    setPartialState({ moving: false });
  };

  const applyZoom = (type) => {
    if (!allowZoom) return;

    let newZoom = zoom;
    if (type === "in") newZoom = zoom + zoomStep;
    if (type === "out") newZoom = zoom - zoomStep;

    if (newZoom < 1) newZoom = 1;

    if (newZoom === 1) setPartialState({ x: 0, y: 0, zoom: 1 });
    else setPartialState({ zoom: newZoom });
  };

  const shockZoom = (e) => {
    if (!allowZoom || !doubleClickZoom) return;
    e.stopPropagation();

    if (zoom > 1) return resetZoom();

    const rect = contRef.current.getBoundingClientRect();
    const xy = getXY(e);
    const ccx = rect.x + rect.width / 2;
    const ccy = rect.y + rect.height / 2;

    const _z = doubleClickZoom;
    const newX = (xy.x - ccx) * -1 * _z;
    const newY = (xy.y - ccy) * -1 * _z;
    setPartialState({ x: newX, y: newY, zoom: _z });
  };

  const applyRotate = (type) => {
    if (!allowRotate) return;
    setPartialState({
      rotate: rotate + (type === "cw" ? 90 : -90),
    });
  };

  const reset = (e) => {
    e.stopPropagation();
    setPartialState({ x: 0, y: 0, zoom: 1, rotate: 0 });
  };

  const exit = (e) => {
    e.stopPropagation();
    if (typeof onClose === "function") onClose(e);
  };

  const canvasClick = (e) => {
    if (clickOutsideToExit && zoom <= 1 && e.target !== imageRef.current)
      exit(e);
  };

  const keyboardNavigation = useCallback(
    (e) => {
      const { key } = e;
      if (key === "ArrowLeft") {
        if (multi && zoom === 1) navigateImage("prev", e);
        else if (zoom > 1) setPartialState({ x: x - 20 });
      } else if (key === "ArrowRight") {
        if (multi && zoom === 1) navigateImage("next", e);
        else if (zoom > 1) setPartialState({ x: x + 20 });
      } else if (key === "ArrowUp" && zoom > 1) {
        setPartialState({ y: y + 20 });
      } else if (key === "ArrowDown" && zoom > 1) {
        setPartialState({ y: y - 20 });
      } else if (key === "+" && allowZoom) {
        applyZoom("in");
      } else if (key === "-" && allowZoom) {
        applyZoom("out");
      } else if (key === "Escape") {
        if (allowReset && shouldShowReset) reset(e);
        else exit(e);
      }
    },
    [
      allowZoom,
      allowReset,
      shouldShowReset,
      zoom,
      x,
      y,
      multi,
      navigateImage,
      exit,
      reset,
    ]
  );

  useEffect(() => {
    document.body.classList.add("lb-open-lightbox");
    if (keyboardInteraction)
      document.addEventListener("keyup", keyboardNavigation);
    return () => {
      document.body.classList.remove("lb-open-lightbox");
      if (keyboardInteraction)
        document.removeEventListener("keyup", keyboardNavigation);
    };
  }, [keyboardInteraction, keyboardNavigation]);

  if (!currentImage) {
    console.warn("LightBox: No image provided.");
    return null;
  }

  return (
    <div className="lb-container">
      <div className="lb-header" style={{ justifyContent: buttonAlign }}>
        {showTitle && currentTitle && (
          <div
            className="lb-title"
            style={{
              display: buttonAlign === "center" ? "none" : "flex",
              order: buttonAlign === "flex-start" ? "2" : "unset",
            }}>
            <span
              title={currentTitle}
              style={{
                textAlign: buttonAlign === "flex-start" ? "right" : "left",
              }}>
              {currentTitle}
            </span>
          </div>
        )}

        {allowReset && shouldShowReset && (
          <div
            title="Reset"
            className="lb-button lb-icon-reset reload"
            onClick={reset}></div>
        )}

        {multi && (
          <>
            <div
              title="Previous"
              className="lb-button lb-icon-arrow prev lb-hide-mobile"
              onClick={(e) => navigateImage("prev", e)}></div>
            <div
              title="Next"
              className="lb-button lb-icon-arrow next lb-hide-mobile"
              onClick={(e) => navigateImage("next", e)}></div>
          </>
        )}

        {allowZoom && (
          <>
            <div
              title="Zoom In"
              className="lb-button lb-icon-zoomin zoomin"
              onClick={() => applyZoom("in")}></div>
            <div
              title="Zoom Out"
              className={`lb-button lb-icon-zoomout zoomout ${
                zoom <= 1 ? "lb-disabled" : ""
              }`}
              onClick={() => applyZoom("out")}></div>
          </>
        )}

        {allowRotate && (
          <>
            <div
              title="Rotate Left"
              className="lb-button lb-icon-rotate rotatel"
              onClick={() => applyRotate("acw")}></div>
            <div
              title="Rotate Right"
              className="lb-button lb-icon-rotate rotater"
              onClick={() => applyRotate("cw")}></div>
          </>
        )}

        <div
          title="Close"
          className="lb-button lb-icon-close close"
          onClick={exit}></div>
      </div>

      <div
        className={`lb-canvas${loading ? " lb-loading" : ""}`}
        ref={contRef}
        onClick={canvasClick}>
        <img
          ref={imageRef}
          draggable="false"
          src={currentImage}
          alt={currentTitle}
          title={currentTitle}
          className={`lb-img${loading ? " lb-loading" : ""}`}
          style={{
            transform,
            cursor: zoom > 1 ? "grab" : "unset",
            transition: moving ? "none" : "all 0.1s",
          }}
          onLoad={() => setPartialState({ loading: false })}
          onDoubleClick={shockZoom}
          onMouseDown={startMove}
          onTouchStart={startMove}
          onMouseMove={duringMove}
          onTouchMove={duringMove}
          onMouseUp={endMove}
          onMouseLeave={endMove}
          onTouchEnd={endMove}
        />

        {/* Mobile controls */}
        <div className="mobile-controls lb-show-mobile">
          {multi && (
            <div
              title="Previous"
              className="lb-button lb-icon-arrow prev"
              onClick={(e) => navigateImage("prev", e)}></div>
          )}
          {allowReset && shouldShowReset && (
            <div
              title="Reset"
              className="lb-button lb-icon-reset reload"
              onClick={reset}></div>
          )}
          {multi && (
            <div
              title="Next"
              className="lb-button lb-icon-arrow next"
              onClick={(e) => navigateImage("next", e)}></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LightBox;
